import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { MedicusLogo } from '@components/MedicusLogo';
import { OtpGate } from '@components/onboarding/OtpGate';
import { FamiliaStep } from '@components/onboarding/FamiliaStep';
import { DdjjStep } from '@components/onboarding/DdjjStep';
import {
  finalizarBis,
  getBisByToken,
  isBisNeedsOtp,
  isBisNoDisponible,
  patchIntegranteBis,
  solicitarOtpBis,
  validarOtpBis,
  type CuotaMs2Bis,
} from '@lib/services/portal-bis.service';
import type { IntegranteDto } from '@lib/services/portal.service';

/**
 * Wizard público de activación de un `legajo_bis` (Fase 2 alta-directa),
 * ruta `/activar-bis/:token`. Tres pasos: datos del/los integrante(s) nuevo(s)
 * → declaración jurada → listo. El OTP (gate inicial) va al TITULAR del legajo
 * destino ya afiliado. Sin plan ni pago (el titular ya está afiliado).
 */

type StepKey = 'datos' | 'ddjj' | 'final';

const BIS_STEPS: { key: StepKey; label: string }[] = [
  { key: 'datos', label: 'Datos del integrante' },
  { key: 'ddjj', label: 'Declaración jurada' },
  { key: 'final', label: 'Listo' },
];

export function ActivateBisPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const [step, setStep] = useState<StepKey>('datos');
  const [globalError, setGlobalError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['onboarding-bis', token],
    queryFn: () => getBisByToken(token),
    enabled: !!token,
    retry: false,
  });

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<IntegranteDto> }) =>
      patchIntegranteBis(token, id, patch),
    onSuccess: () => {
      setGlobalError(null);
      return qc.invalidateQueries({ queryKey: ['onboarding-bis', token] });
    },
    onError: () => setGlobalError('No pudimos guardar los cambios. Reintentá.'),
  });

  const finalizarMut = useMutation({
    mutationFn: () => finalizarBis(token),
    onSuccess: () => {
      setGlobalError(null);
      setStep('final');
    },
    onError: () =>
      setGlobalError('No pudimos finalizar la activación. Revisá la declaración jurada.'),
  });

  if (q.isLoading) {
    return (
      <Shell>
        <p className="text-element-mid">Cargando…</p>
      </Shell>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Shell>
        <p className="status status-error" style={{ display: 'block' }}>
          No pudimos abrir este link de activación. Verificá que sea correcto o pedí uno nuevo a
          RRHH de tu organización.
        </p>
      </Shell>
    );
  }

  const data = q.data;

  if (isBisNoDisponible(data)) {
    return (
      <Shell>
        <p className="status status-error" style={{ display: 'block' }}>
          {data.mensaje}
        </p>
      </Shell>
    );
  }

  if (isBisNeedsOtp(data)) {
    return (
      <Shell>
        <OtpGate
          token={token}
          documento={data.documento}
          primerNombre={data.primerNombre}
          metodos={data.metodos}
          subtitulo="Te enviamos un código para habilitar el ingreso de un nuevo integrante a tu grupo."
          onSolicitar={(m) => solicitarOtpBis(token, m)}
          onValidar={(c) => validarOtpBis(token, c)}
          onValidated={() => qc.invalidateQueries({ queryKey: ['onboarding-bis', token] })}
        />
      </Shell>
    );
  }

  // Ya validó OTP. Si el bis ya estaba activado (re-entrada), mostramos el final.
  const stepActual: StepKey =
    data.bis.estadoActivacion === 'validado' && step !== 'final' ? 'final' : step;

  // Detalle de cuota MS2 (solo aplica si el grupo destino ya tiene MS2).
  const cuota = data.cuotaMs2;

  // Vista final del wizard cuando el grupo tiene MS2: pantalla dedicada
  // (diseño "Validación grupo familiar") con el detalle de cuota, en lugar del
  // "¡Listo!" genérico. El alta YA se disparó al finalizar la DDJJ.
  if (stepActual === 'final' && cuota && cuota.aplica) {
    return <ValidacionFinalMs2 cuota={cuota} />;
  }

  return (
    <Shell numero={data.bis.numero}>
      <Stepper current={stepActual} />

      {!data.titular.destinoDadoDeAlta && stepActual !== 'final' && (
        <p className="status" style={{ display: 'block', marginBottom: 12 }}>
          El titular ({data.titular.nombre}) todavía no está dado de alta en Medicus. Podés
          completar la activación igual: el ingreso del nuevo integrante se procesará cuando el
          titular esté afiliado.
        </p>
      )}

      {globalError && (
        <p className="status status-error" role="alert" style={{ marginBottom: 12 }}>
          {globalError}
        </p>
      )}

      {stepActual === 'datos' && (
        <FamiliaStep
          token={token}
          familiares={data.integrantes}
          observaciones={[]}
          onBack={() => undefined}
          onContinue={() => setStep('ddjj')}
          onUpdate={(id, patch) => patchMut.mutate({ id, patch })}
          busy={patchMut.isPending}
        />
      )}

      {stepActual === 'ddjj' && (
        <DdjjStep
          token={token}
          bis
          onBack={() => setStep('datos')}
          onContinue={() => finalizarMut.mutate()}
        />
      )}

      {stepActual === 'final' && <FinalCardBis />}
    </Shell>
  );
}

function Shell({ children, numero }: { children: ReactNode; numero?: string }) {
  return (
    <div className="onboarding">
      <header className="onboarding__topbar">
        <MedicusLogo width={110} />
        {numero && <span className="onboarding__legajoBadge">Legajo {numero}</span>}
      </header>
      <main className="onboarding__main">{children}</main>
    </div>
  );
}

function Stepper({ current }: { current: StepKey }) {
  const idx = BIS_STEPS.findIndex((s) => s.key === current);
  return (
    <ol
      style={{
        display: 'flex',
        gap: 16,
        listStyle: 'none',
        padding: 0,
        margin: '0 0 22px',
        flexWrap: 'wrap',
      }}
    >
      {BIS_STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li
            key={s.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: done || active ? 'var(--color-primary-darker)' : 'var(--color-element-mid)',
              fontWeight: active ? 700 : 400,
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                border: `1px solid ${done || active ? 'var(--color-primary-darker)' : 'var(--color-outline)'}`,
                background: done ? 'var(--color-primary-darker)' : 'transparent',
                color: done ? '#fff' : 'inherit',
              }}
            >
              {done ? '✓' : i + 1}
            </span>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function FinalCardBis() {
  return (
    <div
      className="onboarding__form"
      style={{ textAlign: 'center', maxWidth: 480, margin: '0 auto' }}
    >
      <CheckCircle2 size={56} strokeWidth={1.8} color="#1a8c3c" style={{ margin: '0 auto 12px' }} />
      <h1 className="heading-3" style={{ marginTop: 0 }}>
        ¡Listo!
      </h1>
      <p className="text-element-mid" style={{ fontSize: 14 }}>
        Recibimos los datos y la declaración jurada del nuevo integrante. Vamos a procesar su
        ingreso al grupo familiar. Ya podés cerrar esta ventana.
      </p>
    </div>
  );
}

/** Formatea un monto ARS sin decimales (es-AR): 72200 → "$72.200". */
function formatArs(n: number): string {
  return `$${Math.round(n).toLocaleString('es-AR')}`;
}

/**
 * Vista FINAL del wizard bis para grupos con MS2 (diseño "Validación grupo
 * familiar"). Pantalla completa (sin stepper, con footer) que reemplaza al
 * "¡Listo!" genérico: muestra, una fila por integrante nuevo, lo que SUMA a la
 * cuota mensual del titular (delta MS2 − base, SIN IVA) + la vigencia de la
 * cobertura. El alta ya se disparó al finalizar la DDJJ; "Aceptar" solo cierra.
 */
function ValidacionFinalMs2({ cuota }: { cuota: Extract<CuotaMs2Bis, { aplica: true }> }) {
  const navy = '#002564';
  const filaBorde = '1px solid rgba(0,37,100,0.06)';
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f2fa',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header style={{ padding: '28px 0 0 32px' }}>
        <MedicusLogo width={110} />
      </header>

      <main
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '32px 16px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 680,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(40px, 7vw, 64px)',
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: '-0.01em',
              }}
            >
              <span style={{ color: navy }}>VALIDACIÓN</span>
              <br />
              <span style={{ color: '#6399f8' }}>GRUPO FAMILIAR</span>
            </h1>
            <p
              style={{
                margin: '16px auto 0',
                maxWidth: 520,
                fontSize: 16,
                lineHeight: 1.5,
                fontWeight: 500,
                color: 'rgba(0,37,100,0.53)',
              }}
            >
              Incorporaste nuevo/s integrante/s a tu grupo familiar. Te brindamos el detalle de la
              gestión:
            </p>
          </div>

          <div
            style={{
              width: '100%',
              background: '#fff',
              borderRadius: 24,
              padding: 'clamp(20px, 4vw, 40px)',
              boxShadow: '0px 4px 8px rgba(0,37,100,0.02), 0px 16px 24px rgba(0,37,100,0.04)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                paddingBottom: 16,
                borderBottom: filaBorde,
                fontSize: 13,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'rgba(0,37,100,0.4)',
              }}
            >
              <span>Nombre/s</span>
              <span style={{ textAlign: 'right' }}>Valor de la cuota (mes)</span>
            </div>

            {cuota.integrantes.map((i, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16,
                  padding: '24px 0',
                  borderBottom:
                    idx === cuota.integrantes.length - 1 ? undefined : filaBorde,
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 700, color: navy }}>{i.nombre}</span>
                <span
                  style={{ fontSize: 16, fontWeight: 600, color: navy, whiteSpace: 'nowrap' }}
                >
                  {formatArs(i.cuotaMensualSinIva)}
                </span>
              </div>
            ))}

            <div
              style={{
                background: 'rgba(0,0,0,0.03)',
                borderRadius: 16,
                padding: 24,
                marginTop: 8,
              }}
            >
              <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: navy }}>
                Vigencia de la cobertura
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.5, color: '#666680' }}>
                A partir del alta en nuestro sistema. Te llegará un correo con los datos de inicio
                de sesión en la app Mi Medicus o en www.medicus.com.ar para acceder a tu credencial
                digital y comenzar a utilizar nuestros servicios.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => window.close()}
            style={{
              background: navy,
              color: '#fff',
              border: 'none',
              borderRadius: 140,
              height: 48,
              width: 200,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Aceptar
          </button>
        </div>
      </main>

      <footer
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          borderTop: filaBorde,
          padding: '24px clamp(16px, 5vw, 80px)',
          fontSize: 14,
          color: 'rgba(0,37,100,0.4)',
        }}
      >
        <span>© Medicus S.A.</span>
        <span>Ayuda y otras consultas</span>
      </footer>
    </div>
  );
}
